import React, { useState } from 'react';

import { Alert } from '@mui/lab';
import { Button, Snackbar, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller, useForm } from 'react-hook-form';

import { appendDataToAirtable } from '../../../utils/integrations/appendDataToAirtable';
import { colors } from '../../../utils/theme';

type FormData = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  practiceDSO: string;
};

const useStyles = makeStyles(() => ({
  formSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    width: '100%',
  },
  input: {
    marginBottom: '16px',
    width: '100%',
  },
  submitButton: {
    margin: '50px 0 70px',
    backgroundColor: colors.PRIMARY,
    color: colors.WHITE,
    fontFamily: 'proxima-nova, sans-serif',
    fontSize: '20px',
    width: '100%',
  },
}));

export const Form = () => {
  const [status, setStatus] = useState<'' | 'success' | 'error'>('');
  const classes = useStyles();
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      practiceDSO: '',
    },
  });

  const onSubmit = (data: FormData) => {
    appendDataToAirtable(
      data.firstName,
      data.lastName,
      data.emailAddress,
      data.practiceDSO,
      {
        onSuccess: () => {
          setStatus('success');
          reset();
        },
        onError: () => setStatus('error'),
      },
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <div className={classes.formSection}>
          <Controller
            name="firstName"
            control={control}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                variant="outlined"
                className={classes.input}
                label={errors.firstName?.message ?? 'First Name'}
                placeholder="XXXXXX"
                error={!!errors.firstName}
              />
            )}
            rules={{
              required: 'Please enter a first name',
              maxLength: {
                value: 50,
                message: 'Please enter a name shorter than 50 characters',
              },
            }}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                variant="outlined"
                className={classes.input}
                label={errors.lastName?.message ?? 'Last Name'}
                placeholder="XXXXXX"
                error={!!errors.lastName}
              />
            )}
            rules={{
              required: 'Please enter a last name',
              maxLength: {
                value: 50,
                message: 'Please enter a name shorter than 50 characters',
              },
            }}
          />
          <Controller
            name="emailAddress"
            control={control}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                variant="outlined"
                className={classes.input}
                label={errors.emailAddress?.message ?? 'Email Address'}
                placeholder="john@gmail.com"
                error={!!errors.emailAddress}
              />
            )}
            rules={{
              required: 'Please enter an email address',
              maxLength: {
                value: 50,
                message: 'Please enter an email shorter than 50 characters',
              },
              pattern: {
                value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                message: 'Email address must be in format john@gmail.com',
              },
            }}
          />
          <Controller
            name="practiceDSO"
            control={control}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                variant="outlined"
                className={classes.input}
                label={errors.practiceDSO?.message ?? 'Practice or DSO'}
                placeholder="XXXX"
                error={!!errors.practiceDSO}
              />
            )}
            rules={{
              required: 'Please enter a Practice or DSO',
              maxLength: {
                value: 50,
                message:
                  'Please enter a Practice or DSO shorter than 50 characters',
              },
            }}
          />
          <Button type="submit" className={classes.submitButton}>
            Get Access - It&apos;s Free
          </Button>
        </div>
      </form>
      <Snackbar
        open={!!status}
        autoHideDuration={6000}
        onClose={() => setStatus('')}
      >
        {status === 'success' ? (
          <Alert severity="success">
            Thank you! Look out for an email from us soon.
          </Alert>
        ) : (
          <Alert severity="error">
            Whoops! Something went wrong, please try again later.
          </Alert>
        )}
      </Snackbar>
    </>
  );
};
